// © Microsoft Corporation. All rights reserved.

import { attr, FASTElement, observable } from "@microsoft/fast-element";
import { MsftFeedNavigation } from "@fast/components";
import { NavigationItemInternal, FeedNavigationData, FeedNavigationLocalizedStrings, FeedNavigationItem, NavigationGroup } from "./Interfaces";
import { isEqual } from "lodash-es";

export class FeedNavigation extends FASTElement {
    @attr public hidePersonalizeButton = false;
    @observable public isTwoLevelNavigationEnabled = false;
    @observable public isSecondaryNav = false;
    @observable public navGroups: NavigationGroup;
    @observable public selectedNavItem: FeedNavigationItem;
    @observable public selectedNavMenuItem: FeedNavigationItem;
    @observable public interestsNavData: FeedNavigationItem;
    @observable public localizedStrings: FeedNavigationLocalizedStrings = {} as FeedNavigationLocalizedStrings;
    @observable public overflowTelemetryTag: string;

    /** Only show the personalize button when this is true. Used to hide the personalize button while in the interests hub */
    @observable public showPersonalizeButton = true;

    public feedNav: MsftFeedNavigation;

    private navItemMap = new Map<string, NavigationItemInternal>();

    /**
     * The connected callback handler
     */
    public connectedCallback() {
        super.connectedCallback();

        if (!this.navGroups) {
            return;
        }

        this.buildNavMap();
        this.setNavItems();
    }

    /**
     * Personalize link click handler
     */
    public onPersonalizeClick = (): void => {
        this.$emit("change", { selectedNav: this.interestsNavData });
    };

    /**
     * Click handler for nav items, listens to change event in FAST feed navigation web component
     */
    public onNavItemClick = (event: CustomEvent): void => {
        const { selectedNav } = event.detail || {};

        if (!selectedNav) {
            return;
        }

        this.$emit("change", { selectedNav });
        event.stopPropagation();
    };

    /** Selected Nav change handler */
    private selectedNavItemChanged(oldValue: FeedNavigationItem, newValue: FeedNavigationItem): void {
        if (!newValue || (oldValue && oldValue.id) === newValue.id) {
            return;
        }

        this.setNavItems();
    }

    /** Selected Nav change handler */
    private selectedNavMenuItemChanged(oldValue: FeedNavigationItem, newValue: FeedNavigationItem): void {
        if (!newValue || (oldValue && oldValue.id) === newValue.id) {
            return;
        }

        this.setNavItems();
    }

    /** is the secondary nav value modified */
    private isSecondaryNavChanged(oldValue: boolean, newValue: boolean): void {
        if (oldValue == newValue) {
            return;
        }

        this.buildNavMap();
        this.setNavItems();
    }

    /** Localized strings change handler */
    private localizedStringsChanged(oldValue, newValue): void {
        if (!this.feedNav) {
            return;
        }

        this.feedNav.localizedStrings = this.localizedStrings || {} as FeedNavigationLocalizedStrings;
    }

    /** InterestsNavData change handler */
    private interestsNavDataChanged(): void {
        if (!this.interestsNavData) {
            return;
        }

        this.navItemMap.set(this.interestsNavData.id, this.interestsNavData);
        this.mapChildItems(this.interestsNavData.children, this.interestsNavData.id);
    }

    /**
     * NavGroups data changed handler, this is internally invoked by Observable implemetation when data changes
     * @param previousValue - previous value
     * @param currentValue - the current value of data input object
     */
    private navGroupsChanged(previousValue: FeedNavigationData, currentValue: FeedNavigationData): void {
        if (isEqual(previousValue, currentValue)) {
            return;
        }

        this.buildNavMap();
        this.setNavItems();
    }

    /**
     * NavGroups data changed handler, this is internally invoked by Observable implemetation when data changes
     * @param previousValue - previous value
     * @param currentValue - the current value of data input object
     */
    private isTwoLevelNavigationEnabledChanged(previousValue: boolean, currentValue: boolean): void {
        if (isEqual(previousValue, currentValue)) {
            return;
        }

        this.setNavItems();
    }

    /**
     * Sets the input data for FAST feed navigation web component
     */
    private setNavItems() {
        if (!this.navGroups || !this.feedNav) {
            return;
        }

        let data = this.isTwoLevelNavigationEnabled ? this.getTwoLevelContextualNavItems() : this.getContextualNavItems();

        if (!data) {
            const { home, feeds } = this.navGroups;
            data = [{ ...home, isActive: (this.selectedNavItem && this.selectedNavItem.id) !== (this.interestsNavData && this.interestsNavData.id) }, ...feeds];
        }

        this.feedNav.data = data;
    }

    private getTwoLevelContextualNavItems(): NavigationItemInternal[] {
        const selectedNavId = this.selectedNavItem && this.selectedNavItem.id;
        const selectedNavMenuId = this.selectedNavMenuItem && this.selectedNavMenuItem.id;
        if (!selectedNavMenuId || !selectedNavId) {
            return;
        }

        const setActiveItem = (navItems: NavigationItemInternal[]) => navItems.map(item => {
            item.isActive = item.id === selectedNavId;
            return item;
        });

        const selectedNav = this.navItemMap.get(selectedNavMenuId);
        const { home, feeds } = this.navGroups;
        const showPipeSeparator = false;
        // Check if selected nav is interests page and handle accordingly
        if (this.interestsNavData &&
            (selectedNavId === this.interestsNavData.id || (selectedNav && selectedNav.parentId === this.interestsNavData.id))) {
            // If interests has child items, show them
            const interestsChildItems = this.interestsNavData.children;
            if (interestsChildItems && interestsChildItems.length) {
                this.showPersonalizeButton = false;
                return setActiveItem([{ ...home, showPipeSeparator: true }, this.interestsNavData, ...interestsChildItems]);
            }

            // if interests does not have child items, retain existing items
            return this.feedNav.data ? this.feedNav.data.map(item => {
                item.isActive = false;
                return item;
            }) : null;
        }

        // Selected nav is not interests, show interests button
        this.showPersonalizeButton = true;

        // If the selected nav is not from config, we set the default feeds as the context.
        // This is our current solution until One Service is able to provide the context for these topics
        // Also we dont show the MyFeed if secondaryNavigation is enabled

        if (!selectedNav) {
            const navItemsToRender = this.isSecondaryNav
                ? [
                    { ...home, showPipeSeparator },
                    { ...this.selectedNavItem },
                    ...feeds
                ]
                : [
                    { ...this.selectedNavItem },
                    ...feeds
                ];
            return setActiveItem(navItemsToRender);
        }

        const { children = [], parentId } = { ...selectedNav };

        // No parent, no children, this is the home page
        if (!parentId && !children.length) {
            const navItemsToRender = this.isSecondaryNav ? [home, ...feeds] : [...feeds];
            return setActiveItem(navItemsToRender);
        }

        // No parent but has children, 1st level contextual nav
        if (!parentId && children.length) {
            const navItemsToRender = this.isSecondaryNav
                ? [{ ...home, showPipeSeparator }, selectedNav, ...children]
                : [selectedNav, ...children];
            return setActiveItem(navItemsToRender);
        }

        // Has parent nav item, find the context
        const parentNav = { ...this.navItemMap.get(parentId) };
        const navItems = children.length ? [selectedNav, ...children] : [...parentNav.children];
        return setActiveItem(navItems);
    }

    /**
     * Returns the nav items for the current context
     */
    private getContextualNavItems(): NavigationItemInternal[] {
        const selectedNavId = this.selectedNavItem && this.selectedNavItem.id;
        if (!selectedNavId) {
            return;
        }

        const setActiveItem = (navItems: NavigationItemInternal[]) => navItems.map(item => {
            item.isActive = item.id === selectedNavId;
            return item;
        });

        const selectedNav = this.navItemMap.get(selectedNavId);
        const { home, feeds } = this.navGroups;
        const showPipeSeparator = !this.isTwoLevelNavigationEnabled;
        // Check if selected nav is interests page and handle accordingly
        if (this.interestsNavData &&
            (selectedNavId === this.interestsNavData.id || (selectedNav && selectedNav.parentId === this.interestsNavData.id))) {
            // If interests has child items, show them
            const interestsChildItems = this.interestsNavData.children;
            if (interestsChildItems && interestsChildItems.length) {
                this.showPersonalizeButton = false;
                return setActiveItem([{ ...home, showPipeSeparator: true }, this.interestsNavData, ...interestsChildItems]);
            }

            // if interests does not have child items, retain existing items
            return this.feedNav.data ? this.feedNav.data.map(item => {
                item.isActive = false;
                return item;
            }) : null;
        }

        // Selected nav is not interests, show interests button
        this.showPersonalizeButton = true;

        // If the selected nav is not from config, we set the default feeds as the context.
        // This is our current solution until One Service is able to provide the context for these topics
        // Also we dont show the MyFeed if secondaryNavigation is enabled

        if (!selectedNav) {
            if (this.isTwoLevelNavigationEnabled) {
                const navItemsToRender = this.isSecondaryNav
                    ? [
                        { ...home, showPipeSeparator },
                        { ...this.selectedNavItem },
                        ...feeds
                    ]
                    : [
                        { ...this.selectedNavItem },
                        ...feeds
                    ];
                return setActiveItem(navItemsToRender);
            }

            return setActiveItem([
                { ...home, showPipeSeparator: true },
                { ...this.selectedNavItem },
                ...feeds
            ]);
        }

        let { children = [], parentId } = { ...selectedNav };

        // No parent, no children, this is the home page
        if (!parentId && !children.length) {
            if (this.isTwoLevelNavigationEnabled) {
                const navItemsToRender = this.isSecondaryNav ? [home, ...feeds] : [...feeds];
                return setActiveItem(navItemsToRender);
            }

            return setActiveItem([home, ...feeds]);
        }

        // No parent but has children, 1st level contextual nav
        if (!parentId && children.length) {
            if (this.isTwoLevelNavigationEnabled) {
                const navItemsToRender = this.isSecondaryNav
                    ? [{ ...home, showPipeSeparator }, selectedNav, ...children]
                    : [selectedNav, ...children];
                return setActiveItem(navItemsToRender);
            }

            return setActiveItem([{ ...home, showPipeSeparator: true }, selectedNav, ...children]);
        }

        // Has parent nav item, find the context
        let parentNav = { ...this.navItemMap.get(parentId) };
        const navItems = children.length ? [selectedNav, ...children] : [...parentNav.children];
        if (!this.isTwoLevelNavigationEnabled) {
            let contextParent;

            while (parentId && parentNav) {
                const { children: parentNavChildren = [] } = parentNav;

                if (!contextParent && parentNavChildren.length && children.length) {
                    contextParent = parentNav;
                    parentNav.showPipeSeparator = showPipeSeparator;
                }

                navItems.unshift(parentNav);

                parentId = parentNav.parentId;

                if (!parentId) {
                    navItems.unshift({ ...home, showPipeSeparator: !contextParent });
                }

                children = parentNav.children;
                parentNav = { ...this.navItemMap.get(parentId) };
            }
        }

        return setActiveItem(navItems);
    }

    /** Builds a Map of nav items for easier discovery of contextual nav */
    private buildNavMap(): void {
        const { home, feeds } = this.navGroups || {};

        if (home) {
            this.navItemMap.set(home.id, home);
        }

        if (!feeds || !feeds.length) {
            return;
        }

        feeds.forEach(feed => {
            this.navItemMap.set(feed.id, feed);

            const { children } = feed;
            this.mapChildItems(children, feed.id);
        });
    }

    private mapChildItems(children: NavigationItemInternal[] = [], parentId: string) {
        children.forEach(child => {
            this.navItemMap.set(child.id, { ...child, parentId });

            const { children: subChild } = child;

            if (subChild && subChild.length) {
                this.mapChildItems(subChild, child.id);
            }
        });
    }
}